import { ref, type ComputedRef, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import request from '@/api/request'
import type { WorkflowKnowledgeContextItem } from '@/utils/workflowKnowledge'
import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'

import { buildKnowledgeSearchQueryForConsistencyAsset } from '@/views/customer/generation-workspace/consistencyAssets'
import {
  buildKnowledgeBlock,
  buildKnowledgeSearchQueryForScene,
  getKnowledgeContextKey,
  mergeKnowledgeContexts as mergeKnowledgeContextItems,
  normalizeKnowledgeResult,
  serializeKnowledgeContextsForWorkflow,
} from '@/views/customer/generation-workspace/knowledgeRadar'
import { normalizePromptFragment } from '@/views/customer/generation-workspace/promptUtils'
import type {
  ConsistencyAssetCollection,
  ConsistencyAssetType,
  KnowledgeSearchResult,
  StoryboardSceneDraft,
} from '@/views/customer/generation-workspace/types'

interface UseWorkspaceKnowledgeOptions {
  projectId: number
  routeFullPath: () => string
  currentStage: ComputedRef<ApprovalStage>
  workflow: Ref<WorkflowInstance | null>
  storyboardScenes: ComputedRef<StoryboardSceneDraft[]>
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  consistencyAssets: Ref<ConsistencyAssetCollection>
  editorContent: Ref<string>
  updateWorkflowInputData: (payload: { input_data: Record<string, any> }) => Promise<WorkflowInstance>
  routerPush: (location: RouteLocationRaw) => Promise<unknown>
  markEditorDirty: () => void
  buildDefaultSceneImagePrompt: (scene: Record<string, any>) => string
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
  warning: (message: string) => void
}

export function useWorkspaceKnowledge(options: UseWorkspaceKnowledgeOptions) {
  const knowledgeQuery = ref('')
  const knowledgeSearching = ref(false)
  const knowledgeResults = ref<KnowledgeSearchResult[]>([])
  const selectedKnowledgeContexts = ref<WorkflowKnowledgeContextItem[]>([])
  const knowledgeSyncing = ref(false)

  async function persistKnowledgeContexts(optionsArg: { silent?: boolean } = {}) {
    if (!options.workflow.value) {
      return
    }

    knowledgeSyncing.value = true
    try {
      options.workflow.value = await options.updateWorkflowInputData({
        input_data: {
          knowledge_contexts: serializeKnowledgeContextsForWorkflow(selectedKnowledgeContexts.value),
        },
      })
      if (!optionsArg.silent) {
        options.success('素材线索已同步到当前流程')
      }
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '同步素材线索失败')
    } finally {
      knowledgeSyncing.value = false
    }
  }

  function mergeKnowledgeContexts(items: WorkflowKnowledgeContextItem[]) {
    const { changed, items: merged } = mergeKnowledgeContextItems(selectedKnowledgeContexts.value, items)
    if (changed) {
      selectedKnowledgeContexts.value = merged
    }

    return changed
  }

  function hydrateKnowledgeContextsFromWorkflow() {
    const workflowContexts = options.workflow.value?.data?.input_data?.knowledge_contexts
    if (!Array.isArray(workflowContexts) || !workflowContexts.length) {
      return
    }

    mergeKnowledgeContexts(workflowContexts)
  }

  async function handleRemoveKnowledgeContext(item: WorkflowKnowledgeContextItem) {
    const key = getKnowledgeContextKey(item)
    selectedKnowledgeContexts.value = selectedKnowledgeContexts.value.filter((current) => getKnowledgeContextKey(current) !== key)
    await persistKnowledgeContexts({ silent: true })
  }

  async function handleSelectKnowledgeResult(result: KnowledgeSearchResult) {
    const normalized = normalizeKnowledgeResult(result)
    if (!normalized) {
      return
    }

    const changed = mergeKnowledgeContexts([normalized])
    if (!changed) {
      options.info('这条素材已经在当前工作台中')
      return
    }

    await persistKnowledgeContexts({ silent: true })
    options.success('素材已加入当前工作台')
  }

  async function handleKnowledgeSearch() {
    if (!knowledgeQuery.value.trim()) {
      options.warning('请输入要检索的素材内容')
      return
    }

    knowledgeSearching.value = true
    try {
      const response = await request.post<{ results?: KnowledgeSearchResult[] }>('/knowledge/query', {
        query: knowledgeQuery.value.trim(),
        top_k: 6,
        kb_type: 'all',
      })
      knowledgeResults.value = Array.isArray(response?.results) ? response.results : []
      if (!knowledgeResults.value.length) {
        options.info('没有找到匹配的参考素材')
      }
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '素材检索失败')
    } finally {
      knowledgeSearching.value = false
    }
  }

  function handleOpenKnowledgeBase() {
    void options.routerPush({
      path: '/knowledge',
      query: {
        projectId: String(options.projectId),
        stage: options.currentStage.value,
        tab: 'project',
        return: options.routeFullPath(),
        q: knowledgeQuery.value.trim() || undefined,
      },
    })
  }

  function handleAppendKnowledgeToEditor(items: WorkflowKnowledgeContextItem[] = selectedKnowledgeContexts.value) {
    if (!items.length) {
      return
    }

    const block = buildKnowledgeBlock(items)
    options.editorContent.value = [options.editorContent.value.trim(), '补充素材线索：', block].filter(Boolean).join('\n\n')
    options.markEditorDirty()
    options.success('素材线索已追加到当前编辑区')
  }

  function buildSceneKnowledgePrompt(
    sceneNumber: number,
    promptOverride?: string,
    extraLines: string[] = [],
    fallbackToSceneDefault = true,
  ) {
    const scene = options.storyboardDraftScenes.value.find((item) => item.scene_number === sceneNumber)
      || options.storyboardScenes.value.find((item) => item.scene_number === sceneNumber)
    const basePrompt = normalizePromptFragment(promptOverride || scene?.background_prompt)
      || (fallbackToSceneDefault ? options.buildDefaultSceneImagePrompt(scene || {}) : '')
    const nonEmptyExtraLines = extraLines.filter((item) => String(item || '').trim())
    if (!selectedKnowledgeContexts.value.length && !nonEmptyExtraLines.length) {
      return basePrompt || undefined
    }

    const knowledgePrompt = selectedKnowledgeContexts.value
      .slice(0, 4)
      .map((item) => `${item.title}：${item.content}`)
      .join('\n')

    return [
      basePrompt,
      ...nonEmptyExtraLines,
      '补充知识约束：',
      knowledgePrompt,
    ].filter(Boolean).join('\n')
  }

  function handleOpenKnowledgeBaseForScene(sceneNumber: number) {
    const scene = options.storyboardDraftScenes.value.find((item) => item.scene_number === sceneNumber)
      || options.storyboardScenes.value.find((item) => item.scene_number === sceneNumber)
    void options.routerPush({
      path: '/knowledge',
      query: {
        projectId: String(options.projectId),
        stage: 'storyboard',
        sceneNumber: String(sceneNumber),
        mode: 'scene-image',
        tab: 'image',
        return: options.routeFullPath(),
        q: buildKnowledgeSearchQueryForScene(scene, knowledgeQuery.value) || undefined,
      },
    })
  }

  function handleOpenKnowledgeBaseForConsistencyAsset(type: ConsistencyAssetType, assetId: string) {
    const asset = options.consistencyAssets.value[type].find((item) => item.id === assetId)
    if (!asset) {
      return
    }

    void options.routerPush({
      path: '/knowledge',
      query: {
        projectId: String(options.projectId),
        stage: 'storyboard',
        assetType: type,
        assetId,
        assetName: asset.name || assetId,
        mode: 'consistency-asset-image',
        tab: 'image',
        return: options.routeFullPath(),
        q: buildKnowledgeSearchQueryForConsistencyAsset(asset) || undefined,
      },
    })
  }

  return {
    buildSceneKnowledgePrompt,
    handleAppendKnowledgeToEditor,
    handleKnowledgeSearch,
    handleOpenKnowledgeBase,
    handleOpenKnowledgeBaseForConsistencyAsset,
    handleOpenKnowledgeBaseForScene,
    handleRemoveKnowledgeContext,
    handleSelectKnowledgeResult,
    hydrateKnowledgeContextsFromWorkflow,
    knowledgeQuery,
    knowledgeResults,
    knowledgeSearching,
    mergeKnowledgeContexts,
    persistKnowledgeContexts,
    selectedKnowledgeContexts,
  }
}